import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { useDrop } from "src/core/drops/hooks";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import {
  IDrop,
  WeekDay
} from "src/core/drops/api/drops.api";
import { IStore } from "src/core/shared/store";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";
import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import { useShutdownLeadTimeExtend } from "src/modules/shutdowns/hooks/useShutdownLeadTimeExtend";

/**
 * Get number of days left for an order cutoff.
 *
 * @param {IOrder} order - order object.
 * @param {IDeliveryCutoffSetting} partOrder
 *
 * @return - Whether the date has passed or not.
 */
const useCutoffByDate = (inputDrop?: IDrop | null) => {
  const [ { drop } ] = useDrop();

  const dropToUse = React.useMemo(() => {
    if (inputDrop) {
      return inputDrop;
    }

    return drop;
  }, [ drop, inputDrop ]);

  const { minimumLeadTime } = useMinimumLeadTime();
  const leadTimeOverrides = useMappedState((store: IStore) => store.global.settings?.leadTimeOverrides);
  const timezone = useTimezone();
  const getShutdownsExtendDays = useShutdownLeadTimeExtend();

  const dateCutOffTime = React.useCallback(
    (deliveryDate: Date, shortLong: "shortest" | "longest" = "shortest", orderVariantId: number, leadTimeGroup?: IProductLeadTimeGroup): Date | null => {
      if (!dropToUse) {
        return null;
      }

      const leadTimes = getDeliveryWeekLeadTimes(dropToUse.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(dropToUse.groupedSettings);

      if (!leadTimes || !defaultCutoff) {
        return null;
      }

      // Only do delivery date validation if it's a date-led order
      if (deliveryDate) {
        const hasMatchingLeadTimeOverride = _.find(leadTimeOverrides, lto => moment.tz(lto.deliveryDate, timezone).isSame(moment.utc(deliveryDate), "day"));

        // return the cutoff override if there is one
        if (hasMatchingLeadTimeOverride) {
          return moment.tz(hasMatchingLeadTimeOverride.cutoffDateTime, timezone).toDate();
        }

        let orderLeadTimes: IProductLeadTimeGroup[] = leadTimeGroup ? [ leadTimeGroup ] : minimumLeadTime ? [ minimumLeadTime ] : [];

        orderLeadTimes = _.uniqBy(_.compact(orderLeadTimes), "name");

        let orderCutoff = shortLong === "shortest" ? moment.tz(deliveryDate, timezone).subtract(minimumLeadTime?.defaultLeadTimeDays || 0) : moment.tz(timezone);
        let hasBeenSet = false;

        for (const leadTime of orderLeadTimes) {
          // get the cutoff time for this lead time
          const deliverydateMoment = moment.tz(deliveryDate, timezone);
          const deliveryDOW: WeekDay = deliverydateMoment.isoWeekday() as WeekDay;
          const deliveryDOWString = deliveryDOW.toString();
          // get the day the cutt of set from
          const orderByDay = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].day;
          const orderByWeek = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].week || 0;
          const cutOffTime = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].cutoffTime;
          const leadTimeCalc = (deliveryDOW - orderByDay + 7) % 7;
          const deliveryCutOff = deliverydateMoment.clone().subtract(leadTimeCalc, "days");

          deliveryCutOff.subtract(orderByWeek, "week");

          if (!cutOffTime) {
            return null;
          }
          const { hours, minutes } = hourMinuteTimeSplit(cutOffTime);

          deliveryCutOff.hours(hours).minutes(minutes);
          const leadTimeCutoff = deliveryCutOff;

          // return the shortest or longets depending on setting
          if (shortLong === "shortest") {
            if (leadTimeCutoff.isSameOrBefore(orderCutoff, "minutes")) {
              orderCutoff = leadTimeCutoff;
              hasBeenSet = true;
            }
          } else if (shortLong === "longest") {
            if (hasBeenSet) {
              if (leadTimeCutoff.isSameOrAfter(orderCutoff, "minutes")) {
                orderCutoff = leadTimeCutoff;
                hasBeenSet = true;
              }
            } else {
              orderCutoff = leadTimeCutoff;
              hasBeenSet = true;
            }
          }
        }

        const daysToExtend = getShutdownsExtendDays(deliveryDate, orderCutoff.toDate(), orderVariantId);

        orderCutoff.subtract(daysToExtend, "day");

        if (hasBeenSet) {
          return orderCutoff.toDate();
        } else if (shortLong === "shortest") {
          return orderCutoff.toDate();
        }
      }

      return null;
    },
    [
      dropToUse,
      leadTimeOverrides,
      minimumLeadTime,
      timezone,
      getShutdownsExtendDays
    ]
  );

  return dateCutOffTime;
};

export default useCutoffByDate;
